/**
 * `expo-application` no browser.
 *
 * - `nativeApplicationVersion`: versão do app (= `version` do package.json, a mesma do
 *   `app.json` do nativo). Pode ser sobrescrita no build por `VITE_APP_VERSION`.
 * - `nativeBuildVersion`: número do build. `VITE_APP_BUILD` no build; sem ele, o mesmo
 *   `versionCode` do `app.json` da versão 4.0.22 ("94").
 * - `applicationId`: identificador próprio do web, para o backend distinguir do Android
 *   (`com.dntec.tirvumais`) e do iOS.
 * - `getAndroidId()`/`getIosIdForVendorAsync()`: WEB: o browser não expõe identificador de
 *   aparelho. Geramos um id aleatório estável por origem, guardado em `localStorage`
 *   (some se o usuário limpar os dados do site — como o ANDROID_ID some num reset).
 */
import { version as versaoPackageJson } from "../../package.json";

const env = import.meta.env as Record<string, string | undefined>;

export const nativeApplicationVersion: string | null = env.VITE_APP_VERSION || versaoPackageJson || null;

export const nativeBuildVersion: string | null = env.VITE_APP_BUILD || "94";

export const applicationId: string | null = "com.dntec.tirvumais.web";

export const applicationName: string | null = "Tirvu+";

const CHAVE_ID_INSTALACAO = "tirvu:id-instalacao";

let idEmMemoria: string | null = null;

function gerarId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID().replace(/-/g, "");
    }
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    }
  } catch {
    // cai no fallback abaixo
  }
  return (Date.now().toString(16) + Math.random().toString(16).slice(2) + Math.random().toString(16).slice(2)).slice(0, 32);
}

/** Id estável da "instalação" web (por origem/perfil do browser). */
function idDaInstalacao(): string {
  if (idEmMemoria) return idEmMemoria;
  try {
    const salvo = localStorage.getItem(CHAVE_ID_INSTALACAO);
    if (salvo) {
      idEmMemoria = salvo;
      return salvo;
    }
    const novo = gerarId();
    localStorage.setItem(CHAVE_ID_INSTALACAO, novo);
    idEmMemoria = novo;
    return novo;
  } catch {
    // WEB: localStorage bloqueado (aba privada/política): id só desta carga de página.
    idEmMemoria = gerarId();
    return idEmMemoria;
  }
}

/** ANDROID_ID do expo-application (síncrono). WEB: id estável gerado por origem. */
export function getAndroidId(): string {
  return idDaInstalacao();
}

/** identifierForVendor do iOS. WEB: o mesmo id estável de `getAndroidId()`. */
export async function getIosIdForVendorAsync(): Promise<string | null> {
  return idDaInstalacao();
}

/** WEB: não há data de instalação no browser — devolve a data em que o id foi criado (aprox.: agora). */
export async function getInstallationTimeAsync(): Promise<Date> {
  return new Date();
}

/** WEB: não há referrer de instalação de loja. */
export async function getInstallReferrerAsync(): Promise<string> {
  return "";
}

export enum ApplicationReleaseType {
  UNKNOWN = 0,
  SIMULATOR = 1,
  ENTERPRISE = 2,
  DEVELOPMENT = 3,
  AD_HOC = 4,
  APP_STORE = 5,
}

export async function getIosApplicationReleaseTypeAsync(): Promise<ApplicationReleaseType> {
  return ApplicationReleaseType.UNKNOWN;
}
